
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleGenAI, Part } from "@google/genai"; // Added Part type
import { Button } from './Button';

// Helper to get API key
const getApiKey = () => {
  if (typeof process === 'undefined' || !process.env || !process.env.API_KEY) {
    console.warn("API_KEY not found in process.env. AI features may be disabled.");
    return undefined;
  }
  return process.env.API_KEY;
};

const GEMINI_MODEL_NAME = "gemini-2.5-flash-preview-04-17";

interface ImagePreview {
  file: File;
  previewUrl: string;
}

// Icons
const StethoscopeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 10-4.773-4.773 3.375 3.375 0 004.774 4.774zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75L18 18M8.625 4.5H7.5a2.25 2.25 0 00-2.25 2.25v.625M8.625 4.5V3M8.625 4.5V2.25m6.75.375V2.25m0 2.25V3m0 1.5v.625a2.25 2.25 0 01-2.25 2.25h-.375" />
    </svg>
);
const PaperAirplaneIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
  </svg>
);
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center space-x-2">
    <div className="w-2 h-2 bg-slate-500 rounded-full animate-pulse delay-0"></div>
    <div className="w-2 h-2 bg-slate-500 rounded-full animate-pulse delay-150"></div>
    <div className="w-2 h-2 bg-slate-500 rounded-full animate-pulse delay-300"></div>
    <span className="text-slate-600">Analyzing symptoms and images...</span>
  </div>
);
const CameraIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
    </svg>
);
const XCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);


// Helper function to convert a File to a GoogleGenerativeAI.Part
async function fileToGenerativePart(file: File): Promise<Part> {
  const base64EncodedDataPromise = new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data URI prefix e.g. "data:image/jpeg;base64,"
      resolve(result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  });
  const base64EncodedData = await base64EncodedDataPromise;
  return {
    inlineData: {
      data: base64EncodedData,
      mimeType: file.type,
    },
  };
}


export const AIDiagnosticAssistantPage: React.FC = () => {
  const [symptoms, setSymptoms] = useState('');
  const [selectedImages, setSelectedImages] = useState<ImagePreview[]>([]);
  const [diagnosisResult, setDiagnosisResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiKey = useRef(getApiKey());
  const fileInputRef = useRef<HTMLInputElement>(null);


  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const newImagePreviews = filesArray.map(file => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      setSelectedImages(prevImages => [...prevImages, ...newImagePreviews].slice(0, 5)); // Limit to 5 images
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    setSelectedImages(prevImages => {
      const imageToRemove = prevImages[indexToRemove];
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.previewUrl);
      }
      return prevImages.filter((_, index) => index !== indexToRemove);
    });
  };

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      selectedImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    };
  }, [selectedImages]);


  const handleGetDiagnosis = async () => {
    if (!symptoms.trim() && selectedImages.length === 0) {
      setError("Please enter symptoms or upload an image.");
      return;
    }
    if (!apiKey.current) {
      setError("API Key is not configured. Cannot perform AI diagnosis.");
      console.error("API Key is missing for AI Diagnostic Assistant.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setDiagnosisResult(null);

    const textPart: Part = { text: `You are an expert diagnostic assistant for Apple devices. A user is reporting the following symptoms and may have provided images:
--- SYMPTOMS START ---
${symptoms || "No textual symptoms provided, please analyze the image(s)."}
--- SYMPTOMS END ---

If images are provided, analyze them in conjunction with the text.
Please provide a diagnostic report with the following sections, clearly formatted using Markdown:
1.  **Potential Causes**: List the most likely causes for these symptoms, considering both text and image information.
2.  **Recommended Diagnostic Steps**: Provide a numbered list of steps a technician can take to further diagnose the issue. Be specific.
3.  **Common Solutions / Next Steps**: Suggest common solutions or next actions based on likely causes. If hardware repair is probable, mention it.

If the symptoms are vague or only images are provided, make your best assessment based on the available information. If necessary, suggest what additional information or tests would be helpful.` };

    try {
      const imageParts: Part[] = await Promise.all(
        selectedImages.map(img => fileToGenerativePart(img.file))
      );
      
      const contents: Part[] = [textPart, ...imageParts];

      const ai = new GoogleGenAI({ apiKey: apiKey.current });
      const response = await ai.models.generateContent({
        model: GEMINI_MODEL_NAME,
        contents: { parts: contents }, // Pass parts array here
      });
      setDiagnosisResult(response.text);
    } catch (e) {
      console.error("Error getting AI diagnosis:", e);
      let errorMessage = "Sorry, an error occurred while generating the diagnosis.";
      if (e instanceof Error) {
          errorMessage += ` Details: ${e.message}`;
      }
      setError(errorMessage);
      setDiagnosisResult(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderFormattedText = (text: string) => {
    let html = text;
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/^(\d+\.\s+)(.*?)(\n|$)/gm, '<li>$2</li>');
    html = html.replace(/(<li>.*?<\/li>)+/gs, (match) => {
        if (match.includes("<ol")) return match; // Prevent double wrapping if already <ol>
        return '<ol class="list-decimal list-inside pl-4 mb-2">' + match + '</ol>';
    });
    html = html.replace(/^([*-]\s+)(.*?)(\n|$)/gm, '<li>$2</li>');
    html = html.replace(/(<li>.*?<\/li>)+/gs, (match) => {
        if (match.includes("<ul") || match.includes("<ol")) return match; // Prevent double wrapping if already <ol> or <ul>
        return '<ul class="list-disc list-inside pl-4 mb-2">' + match + '</ul>';
    });
    html = html.replace(/\n/g, '<br />');
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };


  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-2">
          <StethoscopeIcon className="w-8 h-8 text-[var(--color-primary)] mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-primary)]">AI Diagnostic Assistant (Multimodal)</h1>
            <p className="text-sm text-slate-600">Enter device symptoms and/or upload images for AI-powered diagnostic suggestions.</p>
          </div>
        </div>
      </div>

      {!apiKey.current && (
         <div className="bg-amber-100 border-l-4 border-amber-500 text-amber-700 p-4 rounded-md" role="alert">
          <p className="font-bold">Configuration Notice</p>
          <p>The Gemini API key is not configured. The AI Diagnostic Assistant will not be functional.</p>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-slate-700 mb-3">Enter Device Symptoms</h2>
        <textarea
          value={symptoms}
          onChange={(e) => setSymptoms(e.target.value)}
          placeholder="e.g., iPhone 12 Mini screen unresponsive after drop, device gets very hot during charging..."
          rows={4}
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] resize-y"
          aria-label="Device symptoms input"
          disabled={!apiKey.current || isLoading}
        />

        <div className="mt-4">
            <h3 className="text-lg font-medium text-slate-700 mb-2">Upload Images (Optional, up to 5)</h3>
            <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                className="hidden"
                id="imageUploadInput"
                disabled={!apiKey.current || isLoading || selectedImages.length >= 5}
            />
            <Button
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm flex items-center !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10 mb-3"
                disabled={!apiKey.current || isLoading || selectedImages.length >= 5}
            >
                <CameraIcon /> {selectedImages.length >= 5 ? "Maximum images reached" : "Add Images"}
            </Button>

            {selectedImages.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                    {selectedImages.map((image, index) => (
                        <div key={index} className="relative group border border-slate-200 rounded-md overflow-hidden aspect-square">
                            <img src={image.previewUrl} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                            <button
                                onClick={() => handleRemoveImage(index)}
                                className="absolute top-1 right-1 bg-black bg-opacity-50 text-white p-0.5 rounded-full hover:bg-opacity-75 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label={`Remove image ${index + 1}`}
                            >
                                <XCircleIcon />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>


        <Button
          onClick={handleGetDiagnosis}
          disabled={(!symptoms.trim() && selectedImages.length === 0) || isLoading || !apiKey.current}
          variant="primary"
          className="mt-4 w-full sm:w-auto flex items-center justify-center py-2.5 px-5 text-base"
        >
          <PaperAirplaneIcon className="w-5 h-5 mr-2" />
          {isLoading ? 'Analyzing...' : 'Get AI Diagnosis'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      {isLoading && !diagnosisResult && (
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <LoadingSpinner />
        </div>
      )}

      {diagnosisResult && !isLoading && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-slate-700 mb-4 border-b pb-2">Diagnostic Report</h2>
          <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed">
            {renderFormattedText(diagnosisResult)}
          </div>
        </div>
      )}
    </div>
  );
};
